import type { PdfAnnotation } from '../types';

type MentionableUser = { uid: string; displayName: string };

/** Mirror server `resolveCrmMentionsFromTextWithUsers` for deep-link access checks. */
export function resolveTextMentionUserIds(
  text: string,
  users: MentionableUser[],
): string[] {
  const mentionedIds = new Set<string>();
  const sorted = [...users].sort((a, b) => b.displayName.length - a.displayName.length);

  const firstNameCounts = new Map<string, number>();
  for (const u of users) {
    const fn = u.displayName.trim().split(/\s+/)[0];
    if (fn && fn.length >= 3) {
      const k = fn.toLowerCase();
      firstNameCounts.set(k, (firstNameCounts.get(k) || 0) + 1);
    }
  }

  for (const user of sorted) {
    const escapedName = user.displayName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (new RegExp(`@${escapedName}\\b`, 'i').test(text) && !mentionedIds.has(user.uid)) {
      mentionedIds.add(user.uid);
    }
  }

  for (const user of sorted) {
    const firstName = user.displayName.trim().split(/\s+/)[0];
    if (!firstName || firstName.length < 3) continue;
    if ((firstNameCounts.get(firstName.toLowerCase()) || 0) !== 1) continue;
    if (mentionedIds.has(user.uid)) continue;
    const escaped = firstName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (new RegExp(`@${escaped}\\b`, 'i').test(text)) {
      mentionedIds.add(user.uid);
    }
  }

  return Array.from(mentionedIds);
}

/** True if this user is @mentioned (root or reply) and has not acknowledged the thread. */
export function pdfAnnotationHasUnreadMention(ann: PdfAnnotation, userId: string): boolean {
  if (!pdfAnnotationMentionsUser(ann, userId)) return false;
  return !(ann.readBy ?? []).includes(userId);
}

/**
 * Unread badge for Files / pins / lead feed: any thread activity from someone else that this user
 * has not opened since (readBy). Legacy docs without a `readBy` field fall back to @mention-only
 * so existing annotations do not all appear unread at once.
 */
export function pdfAnnotationHasUnreadForUser(ann: PdfAnnotation, userId: string): boolean {
  if (!userId) return false;
  if ((ann.readBy ?? []).includes(userId)) return false;

  const hasOthersActivity =
    ann.userId !== userId || (ann.replies?.some(r => r.userId !== userId) ?? false);
  if (!hasOthersActivity) return false;

  const hasReadTrackingField = ann.readBy !== undefined;
  if (!hasReadTrackingField) {
    return pdfAnnotationHasUnreadMention(ann, userId);
  }
  return true;
}

/** True when `userId` appears in the root comment or any reply @mentions. */
export function pdfAnnotationMentionsUser(
  ann: Pick<PdfAnnotation, 'mentions' | 'replies'>,
  userId: string,
): boolean {
  if (!userId) return false;
  if (ann.mentions?.some((m) => m.userId === userId)) return true;
  return ann.replies?.some((r) => r.mentions?.some((m) => m.userId === userId)) ?? false;
}

/**
 * Structured @mentions plus plain-text @Name resolution (matches notification email recipients).
 */
export function pdfAnnotationMentionsUserDeep(
  ann: Pick<PdfAnnotation, 'mentions' | 'replies' | 'content'>,
  userId: string,
  crmUsers: MentionableUser[],
): boolean {
  if (pdfAnnotationMentionsUser(ann, userId)) return true;
  const texts = [ann.content || ''];
  for (const r of ann.replies ?? []) {
    if (r.content) texts.push(r.content);
  }
  for (const text of texts) {
    if (resolveTextMentionUserIds(text, crmUsers).includes(userId)) return true;
  }
  return false;
}

/** Project ids where the user is @mentioned on at least one PDF pin. */
export function collectProjectIdsWithPdfMentionsForUser(
  byProjectId: Map<string, PdfAnnotation[]>,
  userId: string | undefined,
  crmUsers: MentionableUser[] = [],
): Set<string> {
  const out = new Set<string>();
  if (!userId) return out;
  byProjectId.forEach((annotations, projectId) => {
    if (!projectId) return;
    if (
      annotations.some((ann) =>
        crmUsers.length > 0
          ? pdfAnnotationMentionsUserDeep(ann, userId, crmUsers)
          : pdfAnnotationMentionsUser(ann, userId),
      )
    ) {
      out.add(projectId);
    }
  });
  return out;
}
